import { create } from 'zustand';
import type { AccountInfo } from '@azure/msal-browser';

/**
 * User information from Microsoft Entra ID
 */
interface User {
  email: string;
  name?: string;
  username?: string;
  tenantId?: string;
  localAccountId?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  mpnAccessToken: string | null;
  mpnRefreshToken: string | null;

  // MSAL-specific methods
  setMsalAuthentication: (account: AccountInfo) => void;
  clearMsalAuthentication: () => void;

  // MPN token management
  setMpnTokens: (accessToken: string, refreshToken?: string) => void;
  getMpnAccessToken: () => string | null;
  clearMpnTokens: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  mpnAccessToken: null,
  mpnRefreshToken: null,

  /**
   * Set authentication state from MSAL account
   * Called when user successfully authenticates with Microsoft Entra ID
   */
  setMsalAuthentication: (account: AccountInfo) => {
    const user: User = {
      email: account.username || account.name || '',
      name: account.name,
      username: account.username,
      tenantId: account.tenantId,
      localAccountId: account.localAccountId,
    };

    set({
      isAuthenticated: true,
      user,
    });
  },

  /**
   * Clear authentication state
   * Called when user logs out or authentication fails
   */
  clearMsalAuthentication: () => {
    // Clear all token storage
    localStorage.removeItem('mpn_access_token');
    localStorage.removeItem('mpn_refresh_token');
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('token');

    set({
      isAuthenticated: false,
      user: null,
      mpnAccessToken: null,
      mpnRefreshToken: null,
    });
  },

  /**
   * Store MPN access and refresh tokens
   * Called after successful token exchange with MPN backend
   */
  setMpnTokens: (accessToken: string, refreshToken?: string) => {
    // Store in localStorage for persistence
    localStorage.setItem('mpn_access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('mpn_refresh_token', refreshToken);
    }

    set({
      mpnAccessToken: accessToken,
      mpnRefreshToken: refreshToken || null,
    });
  },

  /**
   * Get MPN access token from store or localStorage
   */
  getMpnAccessToken: () => {
    const storeToken = get().mpnAccessToken;
    if (storeToken) return storeToken;

    // Fallback to localStorage
    return localStorage.getItem('mpn_access_token');
  },

  /**
   * Clear MPN tokens
   */
  clearMpnTokens: () => {
    localStorage.removeItem('mpn_access_token');
    localStorage.removeItem('mpn_refresh_token');

    set({
      mpnAccessToken: null,
      mpnRefreshToken: null,
    });
  },
}));
